import React, { Fragment, useEffect, useMemo, useState, useRef } from "react";

import {
    ModalSize,
    NotificationToaster,
    WizardModal,
    NotificationInline
} from "components/notifications";
import { Intent, Position } from "@blueprintjs/core";
import PropTypes from "prop-types";
import { FleetService } from "services";
import { FormHeading } from "components/form-fields";
import { AssetStatus } from "components/status/AssetStatus";
import { UserHasAccess, UserAccess } from "components/user-access";
import { ResheduleEvent } from "pages/asset/events/edit/ResheduleEvent";
import { CommentFailEvent } from "pages/asset/events/edit/CommentFailEvent";
import { DefectEvent } from "pages/asset/events/edit/DefectEvent";
import { CostEvent } from "pages/asset/events/edit/CostEvent";
import { DocumentEvent } from "pages/asset/events/edit/DocumentEvent";
import { SignoffEvent } from "pages/asset/events/edit/SignoffEvent";
import { EventWizardProvider, useEventWizard } from "pages/asset/events/shared/EventWizardContext";
import { formatDate } from "pages/asset/events/shared/utils";

import axios from "axios";
import moment from "moment";
import { DefaultEventUpdateRequest } from "pages/asset/events/modals/DefaultModels";
import { Button } from "components/buttons";

function EventEditModalComponent({ 
    isOpen, 
    hideModal,
    onSuccess, 
    eventId, 
    eventTypeId, 
    asset, 
    eventOptions, 
    mappedAssetOptions, 
    loading 
}) {
    const fleetEditAction = "FMS:Edit";
    const userHasFleetEditAction = UserHasAccess(fleetEditAction);
    const inProgressStatusName = "In Progress";
    const inProgressFailedStatusName = "In Progress Failed";
    
    const [currentStepState, setCurrentStepState] = useState({
        currentStep: 0, 
        lastStepIndex: 0,
        canFinish: false
    });
    const additionalSaveSteps = ["Reschedule", "CommentFail", "Defect", "Cost"];
    const signoffStep = "Signoff";
    const rescheduleStep = "Reschedule";
    const [additionalButtonText, setAdditionalButtonText] = useState("");
    
    const { 
        loadingData, 
        saving, 
        event, 
        eventType,
        eventStatus, 
        eventUpdateRequest, 
        isEventDataDirty, 
        stepIsValid, 
        stepsConfig, 
        saveOnEventUpdateRequest, 
        setLoadingData, 
        setEventType, 
        setEvent, 
        setEventStatus, 
        setEventUpdateRequest, 
        setSaving,
        setEventOptions, 
        setSaveOnEventUpdateRequest, 
        setOriginalEventUpdateRequest, 
        specialEventTypeRestricted,
        completeButtonText,
        hasMadeChanges,
        setHasMadeChanges,
        activeStep,
        canSave,
        canFinishEvent
    } = useEventWizard();

    const currentHasMadeChanges = useRef();
    const specialEventTypeRestrictedMessage = rescheduleStep === activeStep ? "This event will be started automatically on the scheduled date." : "This Event will be completed automatically once all requirements are met.";

    // Load wizard data
    useEffect(() => {
        if (!isOpen || !userHasFleetEditAction || !eventId || !eventTypeId) {
            return;
        }

        reload();

    }, [eventId, eventTypeId]);

    // Initial setup of data - must run the first time event is loaded
    useEffect(() => {
        if (!event.eventDate || !!eventUpdateRequest.eventDate) {
            return;
        }

        let vorStartDate = event.vorStartDate;
        let vorEndDate = event.vorEndDate;
        const eventsStatus = eventOptions.eventStatuses.find(s => s.name === event.statusName) || {};

        if (eventType.vorTimer || (eventType.vorTimerFailedOnly && eventsStatus.failedStatus)) {
            if (!vorStartDate) {
                vorStartDate = moment(event.eventDate).subtract(eventType.vorPreDays || 0, 'days').toDate();
            }
            if (!vorEndDate) {
                vorEndDate = moment(event.eventDate).add(eventType.vorPostDays || 0, 'days').toDate();
            }
        }

        const initialUpdateRequest = {
            eventDate: formatDate(event.eventDate, DefaultEventUpdateRequest.eventDate),
            moveAssociatedFutureEvents: false,
            comment: event.comment || DefaultEventUpdateRequest.comment,
            failed: eventsStatus.failedStatus || DefaultEventUpdateRequest.failed,
            vorStartDate: vorStartDate !== null && vorStartDate !== undefined ? formatDate(vorStartDate, DefaultEventUpdateRequest.vorStartDate) : DefaultEventUpdateRequest.vorStartDate,
            vorEndDate: vorEndDate !== null && vorEndDate !== undefined ? formatDate(vorEndDate, DefaultEventUpdateRequest.vorEndDate) : DefaultEventUpdateRequest.vorEndDate,
            eventDefects: event.eventDefects || [],
            eventStatus: eventsStatus.key
        };
        
        setEventUpdateRequest(initialUpdateRequest);
        setOriginalEventUpdateRequest(initialUpdateRequest);
        setEventStatus(eventsStatus);
    }, [event, eventUpdateRequest.eventDate]);

    useEffect(() => {
        setEventOptions(eventOptions);
    }, [eventOptions]);

    useEffect(() => {
        let btnText = completeButtonText;

        // the we are on the signoff step and we haven't completed signoff then we hijack the 
        // complete and close button to perform a signoff and close
        if (activeStep === signoffStep && !stepsConfig.SignoffEvent.stepCompleted) {
            let signoffButtonSuffix = eventUpdateRequest.failed ? "and complete with fail" : "and complete";

            if (!stepsConfig.SignoffEvent.secondSignoffRequired) {
                btnText = `Signoff ${signoffButtonSuffix}`;
            }
            else {            
                btnText = stepsConfig.SignoffEvent.showSecondSignoff ? `Second Signoff ${signoffButtonSuffix}` : "Signoff";
            }
        }

        setAdditionalButtonText(btnText);
    }, [activeStep, stepsConfig.SignoffEvent, completeButtonText])

    useEffect(() => {
        if (saveOnEventUpdateRequest) {
            saveWizard("Event saved successfully after updating event date.").then(
                () => {
                    setOriginalEventUpdateRequest(eventUpdateRequest);
                    setSaveOnEventUpdateRequest(false);
                }
            );
        }
    }, [saveOnEventUpdateRequest, eventUpdateRequest]);

    const finalStep = useMemo(() => {
        return (currentStepState.currentStep >= stepsConfig.TotalSteps - 1)
    }, [currentStepState.currentStep, stepsConfig.TotalSteps]);

    const modalTitle = useMemo(() => {

        if (!event?.name || !eventType?.name || !eventOptions || !asset || !mappedAssetOptions || loading || loadingData) {
            return <Fragment />;
        }

        return (
            <div className="inline-items">
                <FormHeading headingLevel="h2">Edit {event.name} Event for </FormHeading>
                <AssetStatus
                    statusId={asset?.assetLocation?.assetStatus?.id}
                    ownershipId={asset?.assetLocation?.assetOwnership?.id}
                    isLoading={false}
                    statusAttributeMappings={mappedAssetOptions.assetStatusesMappings}
                    ownershipAttributeMappings={mappedAssetOptions.ownershipAttributeMappings}
                    tooltipPosition={Position.RIGHT}
                />
                <FormHeading headingLevel="h2" className="text-intent-primary">{asset?.registration}</FormHeading>
            </div>
        )
    }, [event, eventType, eventOptions, asset, mappedAssetOptions, loading, loadingData]);

    const canProceed = useMemo(() => {
        if (saving || loadingData) {
            return false;
        }

        if (eventStatus?.id && !eventStatus.startedStatus) {
            return stepIsValid && isEventDataDirty
        }

        return stepIsValid;
    }, [stepIsValid, isEventDataDirty, saving, loadingData]);

    // Added this to decouple the standard wizard finish behaviour from the custom behaviour 
    const additionalButtonCanProceed = useMemo(() => {
        if (saving || loadingData) {
            return false;
        }

        // if we are in the signoff step and signoff hasn't been completed the we want to allow the user to signoff and complete
        if (activeStep === signoffStep && !stepsConfig.SignoffEvent.stepCompleted) {
            return canSave;
        }

        return canProceed;
    }, [stepIsValid, saving, loadingData, activeStep, stepsConfig, canSave]);

    const canFinish = useMemo(() => {
        if (specialEventTypeRestricted && rescheduleStep === activeStep && (eventStatus?.key === "PLANNED" || eventStatus?.key === "SCHEDULED")) {
            return true;
        }
        return canFinishEvent && 
            (
                activeStep !== signoffStep || 
                (
                    stepsConfig.SignoffEvent.signoffLater || 
                    stepsConfig.SignoffEvent.hasBeenSignedOff && 
                    (
                        !stepsConfig.SignoffEvent.showSecondSignoff || 
                        stepsConfig.SignoffEvent.hasBeenSecondSignedOff
                    )
                )
            );
    }, [canFinishEvent, activeStep, stepsConfig])

    function reload() {
        setLoadingData(true);

        axios.all([
            FleetService.getEventType(eventTypeId),
            FleetService.getEvent(eventId),
        ]).then(axios.spread(function (getEventTypeResponse, getEventResponse) {
            setEventType(getEventTypeResponse);
            const eventsStatus = eventOptions.eventStatuses.find(s => s.name === getEventResponse.statusName)
            getEventResponse.failed = eventsStatus.failedStatus;
            setEvent(getEventResponse);
            setSaveOnEventUpdateRequest(false);
            setLoadingData(false);
        }), () => {
            NotificationToaster.show(Intent.DANGER, "Could not load the event data needed. Please refresh the page.");
            setLoadingData(false);
        });
    }

    function finishCompleteWizard() {
        if (eventUpdateRequest.failed) {
            let failedStatus = eventOptions.eventStatuses.find(s => s.key === "FAILED");
            return saveWizard("Event set to failed and saved successfully.", failedStatus.id)
        } else {
            let completedStatus = eventOptions.eventStatuses.find(s => s.key === "COMPLETED");
            return saveWizard("Event set to completed and saved successfully.", completedStatus.id)
        }
    }

    function finishCompleteWizardWithSignoffCheck() {
        if (activeStep === signoffStep && !stepsConfig.SignoffEvent.hasBeenSignedOff || stepsConfig.SignoffEvent.showSecondSignoff && !stepsConfig.SignoffEvent.hasBeenSecondSignedOff) {
            return signoffAndTryComplete();
        } 
        return finishCompleteWizard();
    }

    function signoffAndTryComplete() {
        return new Promise((resolve, reject) => {
            stepsConfig.SignoffEvent.onSignOff(stepsConfig.SignoffEvent.showSecondSignoff)
                .then(() => {
                    // if signoff is valid and complete then we can try complete the event
                    if (stepsConfig.SignoffEvent.canComplete) {
                        //Ensure the underlying page reloads
                        currentHasMadeChanges.current = true;
                        finishCompleteWizard().finally(() => {
                            resolve();
                        });
                    } else {
                        setSaving(false);
                        resolve();
                    }
                })
                .catch(() => {
                    setSaving(false);
                    reject();
                });
        });
    }

    function checkIfEventStatusNeedsToChange() {
        let statusId;

        if (!specialEventTypeRestricted && (eventUpdateRequest.eventStatus === "INPROGRESS" || eventUpdateRequest.eventStatus === "INPROGRESSFAILED")) {
            let saveStatus = eventOptions.eventStatuses.find(s => s.key === "INPROGRESS");
            
            if (eventUpdateRequest.failed) {
                saveStatus = eventOptions.eventStatuses.find(s => s.key === "INPROGRESSFAILED");
            }

            statusId = saveStatus && saveStatus.id !== eventStatus?.id ? saveStatus.id : eventStatus?.id;
        }

        return statusId;
    }

    function saveWizard(saveMessage, newEventStatus, updateEventStatus = false) {
        setSaving(true);

        const request = {
            ...eventUpdateRequest,
            eventStatus: newEventStatus || eventStatus?.id,
            vorStartDate: !eventUpdateRequest.vorStartDate || eventUpdateRequest.vorStartDate === "" ? null : eventUpdateRequest.vorStartDate,
            vorEndDate: !eventUpdateRequest.vorEndDate || eventUpdateRequest.vorEndDate === "" ? null : eventUpdateRequest.vorEndDate
        };

        return new Promise((resolve, reject) => {
            FleetService.updateEvent(eventId, request).then(
                () => {
                    setSaving(false);
                    setHasMadeChanges(true);
                    currentHasMadeChanges.current = true;
                    NotificationToaster.show(
                        Intent.SUCCESS,
                        saveMessage ?? "Event saved successfully."
                    );

                    if (eventUpdateRequest.moveAssociatedFutureEvents) {
                        //This needs to be reset
                        setEventUpdateRequest(prev => {
                            return {
                                ...prev,
                                moveAssociatedFutureEvents: false
                            }
                        });
                    }

                    if (updateEventStatus) {
                        setEventStatus(eventOptions.eventStatuses.find(s => s.id === request.eventStatus));
                        reload();
                    }
                    resolve();
                },
                (error) => {
                    //The check for 400 is done in the Service
                    NotificationToaster.show(Intent.DANGER, error);
                    setSaving(false);
                    reject();
                }
            );
        });
    }

    const outstandingRequirementsWarning = useMemo(() => {
        return (<Fragment>This {event?.name} event has the following outstanding requirements: <strong>{event?.outstandingRequirements?.join(", ")}</strong></Fragment>);
    }, [event?.name, event?.outstandingRequirements]);

    const renderOutstandingRequirementsNotification = useMemo(() => {
        return (
            <NotificationInline
                allowClose={false}
                text={outstandingRequirementsWarning}
                intent="info"
                show={
                    event?.outstandingRequirements && event.outstandingRequirements.length > 0 && 
                    (event.statusName === inProgressStatusName || event.statusName === inProgressFailedStatusName)
                }
                icon={null}
            />
        )
    }, [event]);

    function onStepSave(targetEventStatus = "SCHEDULED") {
        switch(activeStep) {
            case "Reschedule": {
                // If we are in a planned state then we want to allow saving, but we don't want to trigger the modal to change to a started state
                // if we are moving to a scheduled state and this method is called from the "Start Event" button then we want to set the event to in progress.
                saveWizard(
                    "Event saved successfully", 
                    eventStatus?.key === "SCHEDULED" ? eventOptions.eventStatuses.find(s => s.key === targetEventStatus)?.id : null, 
                    eventStatus?.key === "SCHEDULED"
                )
                    .then(
                        () => {
                            setOriginalEventUpdateRequest(eventUpdateRequest);
                            setSaveOnEventUpdateRequest(false);
                        }
                    );
                break;
            }
            case "CommentFail": {
                saveWizard("Comment and failure status saved successfully", checkIfEventStatusNeedsToChange())
                    .then(
                        () => {
                            reload();
                            setOriginalEventUpdateRequest(eventUpdateRequest);
                            setSaveOnEventUpdateRequest(false);
                        }
                    );
                break;
            }
            case "Defect": {
                stepsConfig.DefectEvent.onSave();
                break;
            }
            case "Cost": {
                stepsConfig.CostEvent.onSave();
                break;
            }
        }
    }

    const additionalButtons = useMemo(() => {
        const addButtons = [];

        const saveButtonRescheduleStatuses = ["PLANNED", "SCHEDULED"];

        if (specialEventTypeRestricted && rescheduleStep === activeStep && (eventStatus?.key === "PLANNED" || eventStatus?.key === "SCHEDULED")) {
            //You can't start these type of events so it should just show the save and close button.
            return addButtons;
        }

        if (additionalSaveSteps.includes(activeStep) && (activeStep !== rescheduleStep || saveButtonRescheduleStatuses.includes(eventStatus?.key))) {
            addButtons.push(
                <Button
                    key="save-btn"
                    intent="primary"
                    loading={loading}
                    text="Save"
                    onClick={() => onStepSave()}
                    disabled={saving || !canSave}
                    id="finish-btn"
                />
            );
        }

        if (rescheduleStep === activeStep && (eventStatus?.key === "PLANNED" || eventStatus?.key === "SCHEDULED") && eventUpdateRequest?.eventStatus === "SCHEDULED") {
            addButtons.push(
                <Button
                    key="start-event-btn"
                    intent="primary"
                    loading={loading}
                    text="Start Event"
                    onClick={() => onStepSave("INPROGRESS")}
                    disabled={saving || !canSave}
                    id="start-event-btn"
                />
            );
        }

        return (addButtons);
    }, [activeStep, stepsConfig, canSave, eventStatus, saving]);

    function onCancel() {
        if ((hasMadeChanges || currentHasMadeChanges.current) && (onSuccess instanceof Function)) {
            onSuccess();
        }
        hideModal();
    }

    return (
        <UserAccess
            perform={fleetEditAction}
            yes={() => (
                <WizardModal
                    isOpen={isOpen}
                    title={modalTitle}
                    header={renderOutstandingRequirementsNotification}
                    canProceed={canProceed}
                    onFinish={() => saveWizard("Event saved successfully.", checkIfEventStatusNeedsToChange())}
                    onCancel={onCancel}
                    isDirty={isEventDataDirty}
                    size={ModalSize.LARGE}
                    loading={loadingData || loading}
                    canFinish={canFinish}
                    additionalButtons={additionalButtons}
                    additionalCompleteButton={{
                        text: additionalButtonText,
                        onClick: finishCompleteWizardWithSignoffCheck,
                        canProceed: additionalButtonCanProceed,
                        canCloseModalOnSuccess: activeStep !== signoffStep || stepsConfig.SignoffEvent.canComplete,
                        show: additionalButtonText?.length > 0 &&
                            currentStepState.currentStep >= stepsConfig.TotalSteps - 1 &&
                            (!stepsConfig.signoffRequired ||
                            (activeStep === signoffStep && 
                            !stepsConfig.SignoffEvent.hasBeenSignedOff || 
                            stepsConfig.SignoffEvent.showSecondSignoff && !stepsConfig.SignoffEvent.hasBeenSecondSignedOff))
                    }}
                    onStepChange={(step, _, __, lastStepIndex, canFinishOnStep) => {
                        setCurrentStepState(prev => {
                            return {
                                ...prev,
                                currentStep: step,
                                lastStepIndex: lastStepIndex,
                                canFinish: canFinishOnStep
                            };
                        });
                    }}
                    notificationMessage={specialEventTypeRestricted && finalStep && activeStep !== signoffStep ? specialEventTypeRestrictedMessage : null}
                    notificationIntent={Intent.NONE}
                >                        
                    {!stepsConfig.ResheduleEvent.skipStep ? <ResheduleEvent
                        key="edit-event-reshedule"
                    /> : null}

                    {!stepsConfig.CommentFailEvent.skipStep ? <CommentFailEvent
                        key="edit-event-comment-fail"
                    /> : null}

                    {!stepsConfig.DefectEvent.skipStep ? <DefectEvent
                        key="edit-event-defect"
                    /> : null}

                    {!stepsConfig.CostEvent.skipStep ? <CostEvent
                        key="edit-event-cost"
                    /> : null}

                    {!stepsConfig.CostDocumentEvent.skipStep ? <DocumentEvent
                        key="edit-event-cost-document"
                        mode="cost"
                    /> : null}

                    {!stepsConfig.DocumentEvent.skipStep ? <DocumentEvent
                        key="edit-event-document"
                        mode="document"
                    /> : null}

                    {!stepsConfig.SignoffEvent.skipStep ? <SignoffEvent
                        key="edit-event-signoff"
                    /> : null}
                </WizardModal>
            )}
        />
    );
}

export function EventEditModal(
    {
        isOpen = false,
        hideModal = () => { },
        onSuccess = () => { },
        ...props
    }
) {
    return (
        <EventWizardProvider>
            <EventEditModalComponent
                isOpen={isOpen}
                hideModal={hideModal}
                onSuccess={onSuccess}
                {...props}
            />
        </EventWizardProvider>
    )
}

EventEditModal.propTypes = {
    isOpen: PropTypes.bool,
    hideModal: PropTypes.func,
    onSuccess: PropTypes.func.isRequired,
    eventId: PropTypes.number,
    eventTypeId: PropTypes.number,
    asset: PropTypes.object,
    eventOptions: PropTypes.object,
    mappedAssetOptions: PropTypes.object,
    loading: PropTypes.bool
};